import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  ButtonProps,
  HStack,
  Text,
  Popover,
  PopoverProps,
  Portal,
  Tag,
  useDisclosure,
  useOutsideClick,
  PopoverTrigger,
} from "@chakra-ui/react";
import { formatInTimeZone } from "date-fns-tz";
import React, { useRef } from "react";
import { useCloseWhenOutsideViewport } from "../../../hooks/useCloseWhenOutsideViewport";
import { useCurrentBreakpoint } from "../../../hooks/useCurrentBreakpoint";
import { useOverlayPortalContainer } from "../../modals/OverlayPortalContainerContext";
import { DateRangePickerDualPopoverContent } from "./DateRangePickerDualPopoverContent";
import { DateRangePickerSinglePopoverContent } from "./DateRangePickerSinglePopoverContent";

export type DatePickerRange = {
  from: Date;
  to: Date;
};

export type DateRangePickerPreset = {
  key: string;
  label: string;
  dateRange: DatePickerRange;
};

export type DateRangePickerProps = {
  dateRange?: DatePickerRange;
  onChange: (dateRange: DatePickerRange | undefined) => void;
  latestDate: Date;
  earliestDate: Date;
  presetTitle: string;
  presetDateRanges?: DateRangePickerPreset[];
  timeZone: string;
  buttonProps: ButtonProps;
  popoverProps: PopoverProps;
  isDisabled?: boolean;
};

/**
 * Select a date range, day-only (no time components).
 *
 * The date range returned is always a standard UTC Date, where both the `from`
 * and `to` will be set to the start of the day in the date pickers timezone.
 *
 * If only a single date is selected, then both the `from` and `to` will end
 * up with the same Date value, as they are both on the same day.
 *
 * Small mobile screens get a single calendar view, and larger screens get a dual.
 */
export function DateRangePicker({
  dateRange,
  onChange,
  presetTitle,
  presetDateRanges,
  earliestDate,
  latestDate,
  timeZone,
  buttonProps,
  popoverProps,
  isDisabled,
}: DateRangePickerProps): React.ReactElement {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();

  const isMobile = useCurrentBreakpoint().isAtMost("sm");

  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const portalContainerRef = useOverlayPortalContainer();

  // the default popover implementation doesn't always close even when it lost
  // focus, so we manage ourselves.
  useOutsideClick({
    ref: contentRef,
    handler: (event) => {
      const target = event.target as Node | null;

      // a click into the trigger button should still not close the popover
      if (triggerRef.current?.contains(target)) {
        return;
      }

      onClose();
    },
  });

  const handleSelectRange = (nextDateRange: DatePickerRange | undefined) => {
    if (!nextDateRange) {
      onChange(undefined);
    } else {
      onChange(clampDateRange(nextDateRange, earliestDate, latestDate));
    }
  };

  const displayValue =
    dateRange ?
      `${formatInTimeZone(dateRange.from, timeZone, "d MMM yyyy")} - ${formatInTimeZone(dateRange.to, timeZone, "d MMM yyyy")}`
    : "Date";

  useCloseWhenOutsideViewport({
    isOpen,
    targetRef: triggerRef,
    onClose,
  });

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom"
      autoFocus={false}
      returnFocusOnClose={false}
      isLazy
      lazyBehavior="unmount"
      modifiers={[
        {
          // allow the popper to overlap the trigger button to stay inside the viewport
          name: "preventOverflow",
          options: {
            altAxis: true,
            tether: false,
            padding: isMobile ? 0 : 4,
          },
        },
      ]}
      {...popoverProps}
    >
      <PopoverTrigger>
        <Button
          ref={triggerRef}
          variant="outline"
          borderColor={dateRange ? "gray.500" : undefined}
          fontWeight={dateRange ? "medium" : "normal"}
          color={dateRange ? "gray.700" : "gray.500"}
          isDisabled={isDisabled}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (isDisabled) {
              return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();

              if (!isOpen) {
                onOpen();
              }
            }
          }}
          onBlur={(e) => {
            // close if the user clicks outside the popover, or if they
            // navigate away using the keyboard
            if (
              isOpen &&
              !triggerRef.current?.contains(e.relatedTarget) &&
              !contentRef.current?.contains(e.relatedTarget)
            ) {
              onClose();
            }
          }}
          {...buttonProps}
        >
          <HStack justifyContent="space-between" w="full">
            <Text>{displayValue}</Text>

            {dateRange && (
              <Tag
                size="sm"
                colorScheme="primary"
                variant="subtle"
                borderRadius="full"
              >
                {"✓"}
              </Tag>
            )}
            <ChevronDownIcon />
          </HStack>
        </Button>
      </PopoverTrigger>
      <Portal containerRef={portalContainerRef}>
        {isMobile ?
          <DateRangePickerSinglePopoverContent
            ref={contentRef}
            dateRange={dateRange}
            timeZone={timeZone}
            earliestDate={earliestDate}
            latestDate={latestDate}
            presetTitle={presetTitle}
            presetDateRanges={presetDateRanges}
            onSelectRange={handleSelectRange}
            onClear={() => onChange(undefined)}
            onClose={onClose}
          />
        : <DateRangePickerDualPopoverContent
            ref={contentRef}
            dateRange={dateRange}
            timeZone={timeZone}
            earliestDate={earliestDate}
            latestDate={latestDate}
            presetTitle={presetTitle}
            presetDateRanges={presetDateRanges}
            onSelectRange={handleSelectRange}
            onClear={() => onChange(undefined)}
            onClose={onClose}
          />
        }
      </Portal>
    </Popover>
  );
}

function clampDateRange(
  dateRange: DatePickerRange,
  earliestDate?: Date,
  latestDate?: Date,
): DatePickerRange {
  return {
    from: clampDate(dateRange.from, earliestDate, latestDate),
    to: clampDate(dateRange.to, earliestDate, latestDate),
  };
}

function clampDate(date: Date, earliestDate?: Date, latestDate?: Date): Date {
  if (earliestDate && date < earliestDate) {
    return earliestDate;
  }

  if (latestDate && date > latestDate) {
    return latestDate;
  }

  return date;
}
